import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2 } from 'lucide-react';
import {
  customerName,
  customerPhone,
  formatCurrency,
  formatDateTime,
  formatDisplayNumber,
  formatEnum,
  formatOrder,
} from './promotion-report-format';

/** A column heading reads as one line; the body decides the column's width. */
const headerCell = { padding: '8px 12px', whiteSpace: 'nowrap' };
const bodyCell = { padding: '8px 12px' };
const nowrapCell = { ...bodyCell, whiteSpace: 'nowrap' };

export default function PromotionDetailTable({
  items = [],
  total,
  isLoadingMore,
  hasMoreData,
  onLoadMore,
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Promotion Usage Detail</CardTitle>
        <p className="text-sm text-muted-foreground">
          One row per promotion applied to an order, newest first.
        </p>
      </CardHeader>
      <CardContent>
        <div className="border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead style={headerCell}>Date</TableHead>
                  <TableHead style={headerCell}>Order</TableHead>
                  <TableHead style={headerCell}>Display #</TableHead>
                  <TableHead style={headerCell}>Promotion</TableHead>
                  <TableHead style={headerCell}>Type</TableHead>
                  <TableHead style={headerCell}>Channel</TableHead>
                  <TableHead style={headerCell}>Customer</TableHead>
                  <TableHead style={headerCell}>Status</TableHead>
                  <TableHead className="text-right" style={headerCell}>
                    Discount
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      No promotion usage found
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item, index) => {
                    const name = customerName(item);
                    const phone = customerPhone(item);

                    return (
                      <TableRow
                        key={`${item.orderId}-${item.promotionUuid}-${index}`}
                        className={`hover:bg-muted/70 transition-colors ${
                          index % 2 === 0 ? 'bg-background' : 'bg-muted/20'
                        }`}
                      >
                        <TableCell style={nowrapCell}>
                          {formatDateTime(item.createdAt)}
                        </TableCell>
                        <TableCell style={nowrapCell}>
                          {formatOrder(item)}
                        </TableCell>
                        <TableCell style={nowrapCell}>
                          {formatDisplayNumber(item)}
                        </TableCell>
                        <TableCell className="font-medium" style={bodyCell}>
                          {item.promotionName}
                        </TableCell>
                        <TableCell style={nowrapCell}>
                          {formatEnum(item.type)}
                        </TableCell>
                        <TableCell style={nowrapCell}>
                          {formatEnum(item.channel)}
                        </TableCell>
                        <TableCell style={nowrapCell}>
                          {name || phone ? (
                            <div className="flex flex-col leading-tight">
                              <span>{name || phone}</span>
                              {name && phone && (
                                <span className="text-xs text-muted-foreground">
                                  {phone}
                                </span>
                              )}
                            </div>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell style={bodyCell}>
                          <Badge variant="outline">
                            {formatEnum(item.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right" style={nowrapCell}>
                          {formatCurrency(item.discountAmount)}
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex flex-col items-center gap-4 mt-4 no-print">
          <div className="text-sm text-muted-foreground">
            Showing {items.length} of {total || items.length} usages
          </div>

          {hasMoreData && (
            <Button
              onClick={onLoadMore}
              disabled={isLoadingMore}
              variant="outline"
              className="w-full sm:w-auto"
            >
              {isLoadingMore ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </>
              ) : (
                'Load More'
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
